import { Form, FormInstance, Input, Select } from 'antd'
import { useEffect, useState } from 'react'
import { useGetDetailBank } from '../../api/bank'
import { ReactComponent as CheckSelectIcon } from '../../common/images/svg/CheckSelect.svg'
import { ReactComponent as ArrowIcon } from '../../common/images/svg/SuffixSelectIcon.svg'
import { CustomButton } from '../../components/button/buttonCustom'
import { ButtonOutlinedCustom } from '../../components/button/buttonOulined'
import {
  addressRules,
  clearFieldErrorMessageOnChange,
  nameRules,
} from '../../utils/form.constants'
import { useMetadataCountries } from '../../utils/hooks/useMetadataCountries'
import useResponsive from '../../utils/hooks/useResponsive'
import { IFormBank } from './bank-management'

interface IFormBankProps {
  bankId: number | null
  handleCancel: () => void
  form: FormInstance<IFormBank>
  onSubmitWallet: (values: IFormBank) => void
  isLoading: boolean
}

const FormBank = ({
  bankId,
  handleCancel,
  form,
  onSubmitWallet,
  isLoading,
}: IFormBankProps) => {
  const { isTablet } = useResponsive()
  const { data: detailBank } = useGetDetailBank(bankId)
  const { metadataCountries } = useMetadataCountries()
  const [searchCountry, setSearchCountry] = useState<string>('')
  const requiredFields = ['name', 'swift', 'legalAddress', 'country']
  const [isAllFieldsFilled, setIsAllFieldsFilled] = useState(bankId ? false : true)
  const checkAllFieldsFilled = () => {
    const values = form.getFieldsValue(requiredFields as any)
    return requiredFields.every((field) => !!values[field])
  }

  const handleChangeCountry = (value: string) => {
    setSearchCountry(value)
  }

  useEffect(() => {
    if (!bankId || !detailBank) return
    form.setFieldsValue({
      name: detailBank.data.name || '',
      swift: detailBank.data.swift || '',
      legalAddress: detailBank.data.legalAddress || '',
      country: detailBank.data.country || '',
    })
    setSearchCountry(detailBank.data.country)
  }, [bankId, detailBank])

  return (
    <Form
      form={form}
      onFinish={onSubmitWallet}
      onValuesChange={() => {
        clearFieldErrorMessageOnChange(form)
        setIsAllFieldsFilled(checkAllFieldsFilled())
      }}>
      <div className='form-edit-wallet-content'>
        <Form.Item
          label='Name'
          name='name'
          rules={nameRules}
          validateTrigger='onBlur'>
          <Input placeholder='Enter the full name of the bank' />
        </Form.Item>
        <Form.Item
          label='SWIFT Code'
          name='swift'
          validateTrigger='onBlur'
          rules={[
            { required: true, message: 'This field is required' },
            {
              max: 30,
              message: 'Maximum 30 characters',
              validateTrigger: 'onBlur',
            },
            {
              min: 6,
              message: 'Minimum 6 characters',
              validateTrigger: 'onBlur',
            },
            {
              validator: (_, value) => {
                if (!value) return Promise.resolve()
                if (!/^[A-Z0-9]+$/.test(value)) {
                  return Promise.reject('Uppercase letters and numbers only')
                }
                return Promise.resolve()
              },
            },
          ]}>
          <Input placeholder='Enter the bank’s SWIFT code for international transfers' />
        </Form.Item>
        <Form.Item
          label='Address'
          name='legalAddress'
          rules={addressRules}
          validateTrigger='onBlur'>
          <Input placeholder='Enter the registered address or headquarters of the bank' />
        </Form.Item>
        <Form.Item
          style={{ width: '100%' }}
          label='Country'
          name='country'
          rules={[{ required: true, message: 'This field is required' }]}
          validateTrigger='onBlur'>
          <Select
            showSearch
            style={{ width: '100%' }}
            placeholder='Select the country where the bank is located'
            suffixIcon={<ArrowIcon />}
            optionLabelProp='label'
            onChange={handleChangeCountry}
            filterOption={(input, option) => {
              const label = option?.label
              if (typeof label === 'string') {
                return label.toLowerCase().includes(input.toLowerCase())
              }
              return false
            }}>
            {metadataCountries.map((item) => (
              <Select.Option
                key={item.value}
                value={item.value}
                label={item.label}>
                <span
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '100%',
                    gap: 2,
                  }}>
                  <span
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      flex: 1,
                    }}>
                    {item.label}
                  </span>
                  {searchCountry === item.value && (
                    <CheckSelectIcon style={{ flexShrink: 0 }} />
                  )}
                </span>
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </div>
      <div className='form-edit-wallet-footer'>
        <ButtonOutlinedCustom
          text={'Cancel'}
          size={isTablet ? 'medium' : 'large'}
          width={'50%'}
          onClick={handleCancel}
          isLoading={isLoading}
        />
        <CustomButton
          type='submit'
          text={'Save'}
          size={isTablet ? 'medium' : 'large'}
          width={'50%'}
          isLoading={isLoading}
          disabled={!isAllFieldsFilled}
        />
      </div>
    </Form>
  )
}

export default FormBank
